import { Bus, GraduationCap, MapPin, TrainFront } from 'lucide-react';
import { useMemo, useState } from 'react';
import AreaMap from './AreaMap';
import Notice from '../Notice';
import StatusChip from '../StatusChip';
import { tx } from '../../i18n';
import { formatDate, formatNumber } from '../../lib/format';
import { num } from '../../lib/values';
import type { AreaResponse, AreaSchool } from '../../types/api';

interface AreaResultsProps {
  area: AreaResponse;
  /** Which schools the filters count; the map rings and the nearest-school column follow this. */
  isWanted: (school: AreaSchool) => boolean;
}

/**
 * The answer, however the question arrived.
 *
 * <p>The same block serves the filter screen and the sentence search, because they differ only in
 * how the filters were arrived at - and a reader who typed a sentence deserves the same coverage
 * figures, the same map and the same list as one who used the dropdowns.
 */
export default function AreaResults({ area, isWanted }: AreaResultsProps) {
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const matching = useMemo(
    () =>
      area.points
        .filter((point) => point.matches)
        .sort((a, b) => (a.school?.distanceMetres ?? 99_999) - (b.school?.distanceMetres ?? 99_999)),
    [area],
  );

  const { coverage } = area;
  const share = coverage.postcodesKnown > 0
    ? Math.round((coverage.postcodesCrawled / coverage.postcodesKnown) * 100)
    : 0;

  return (
    <>
      <p className="area-summary">
        <StatusChip
          status={area.matches > 0 ? 'good' : 'attention'}
          label={tx(
            `${area.matches} postcodes match`,
            `${area.matches} posta kodu uyuyor`,
            `${area.matches} kodów pasuje`,
            `${area.matches} códigos postais correspondem`,
            `${area.matches} códigos postales cumplen`,
          )}
        />
        <span className="small muted">
          {tx(
            `Read ${coverage.postcodesCrawled} of ${coverage.postcodesKnown} postcodes (${share}%), holding ${formatNumber(coverage.certificates)} certificates between them${coverage.postcodesEmpty > 0 ? `; ${coverage.postcodesEmpty} hold none at all` : ''}.`,
            `${coverage.postcodesKnown} posta kodundan ${coverage.postcodesCrawled} tanesi okundu (%${share}); toplam ${formatNumber(coverage.certificates)} sertifika${coverage.postcodesEmpty > 0 ? `; ${coverage.postcodesEmpty} tanesinde hiç sertifika yok` : ''}.`,
            `Odczytano ${coverage.postcodesCrawled} z ${coverage.postcodesKnown} kodów (${share}%), zawierających łącznie ${formatNumber(coverage.certificates)} świadectw${coverage.postcodesEmpty > 0 ? `; ${coverage.postcodesEmpty} nie ma żadnego` : ''}.`,
            `Lidos ${coverage.postcodesCrawled} de ${coverage.postcodesKnown} códigos (${share}%), com ${formatNumber(coverage.certificates)} certificados no total${coverage.postcodesEmpty > 0 ? `; ${coverage.postcodesEmpty} não têm nenhum` : ''}.`,
            `Leídos ${coverage.postcodesCrawled} de ${coverage.postcodesKnown} códigos (${share}%), con ${formatNumber(coverage.certificates)} certificados entre todos${coverage.postcodesEmpty > 0 ? `; ${coverage.postcodesEmpty} no tienen ninguno` : ''}.`,
          )}
          {coverage.completedAt
            ? ' '
              + tx(
                  `Read ${formatDate(coverage.completedAt)}.`,
                  `Okuma tarihi ${formatDate(coverage.completedAt)}.`,
                  `Odczyt z ${formatDate(coverage.completedAt)}.`,
                  `Lido em ${formatDate(coverage.completedAt)}.`,
                  `Leído el ${formatDate(coverage.completedAt)}.`,
                )
            : ''}
        </span>
      </p>

      {area.bounds ? (
        <AreaMap
          bounds={area.bounds}
          points={area.points}
          schools={area.schools}
          schoolMetres={area.filters.schoolMetres}
          requireSchool={area.filters.requireSchool}
          isWanted={isWanted}
          highlighted={highlighted}
          onHighlight={setHighlighted}
        />
      ) : null}

      {area.ungradedSchools > 0 && area.filters.gradedGoodOnly ? (
        <p className="small muted">
          {tx(
            `${area.ungradedSchools} schools here carry no overall Ofsted judgement — since the single-word grades were withdrawn, an inspection produces grades per area and no overall one. They are drawn, but a "Good or Outstanding" filter cannot include them.`,
            `Buradaki ${area.ungradedSchools} okulun genel Ofsted notu yok — tek kelimelik notlar kaldırıldığından beri denetimler alan bazında not veriyor, genel not vermiyor. Haritada çizilirler ama "Good veya Outstanding" filtresi onları kapsayamaz.`,
            `${area.ungradedSchools} szkół nie ma ogólnej oceny Ofsted — od czasu wycofania jednowyrazowych ocen inspekcja daje oceny cząstkowe, a nie ogólną. Są rysowane, ale filtr „Good lub Outstanding" nie może ich objąć.`,
            `${area.ungradedSchools} escolas não têm avaliação global do Ofsted — desde que as classificações de uma palavra foram retiradas, a inspeção dá notas por área e nenhuma global. São desenhadas, mas um filtro "Good ou Outstanding" não as pode incluir.`,
            `${area.ungradedSchools} colegios no tienen valoración global de Ofsted: desde que se retiraron las calificaciones de una palabra, la inspección da notas por área y ninguna global. Se dibujan, pero un filtro de "Good u Outstanding" no puede incluirlos.`,
          )}
        </p>
      ) : null}

      <section className="stack stack--tight" aria-labelledby="area-list-heading">
        <h2 id="area-list-heading" className="card__title">
          {tx('The postcodes that match', 'Uyan posta kodları', 'Pasujące kody pocztowe', 'Os códigos que correspondem', 'Los códigos que cumplen')}
        </h2>

        {matching.length === 0 ? (
          <Notice
            tone="warning"
            title={tx('Nothing here matches', 'Burada uyan yok', 'Nic tu nie pasuje', 'Nada corresponde aqui', 'Aquí no cumple nada')}
          >
            {tx(
              'Loosening one filter usually finds something: a band lower, or a few hundred metres further from the school. An empty result is a fact about this area, not a problem with the search.',
              'Genelde tek bir filtreyi gevşetmek yeter: bir band aşağısı ya da okula birkaç yüz metre daha uzak. Boş sonuç, aramanın değil bu alanın bir gerçeğidir.',
              'Zwykle wystarczy poluzować jeden filtr: klasa niżej albo kilkaset metrów dalej od szkoły. Pusty wynik to fakt o tym obszarze, a nie problem z wyszukiwaniem.',
              'Basta normalmente aliviar um filtro: uma classe abaixo, ou algumas centenas de metros mais longe da escola. Um resultado vazio é um facto sobre esta área, não um problema da pesquisa.',
              'Normalmente basta con aflojar un filtro: una letra menos, o unos cientos de metros más lejos del colegio. Un resultado vacío es un hecho sobre esta zona, no un fallo de la búsqueda.',
            )}
          </Notice>
        ) : (
          <ul className="area-list">
            {matching.map((point) => {
              const qualifying = num(point.qualifying) ?? 0;
              const certificates = num(point.certificates) ?? 0;
              return (
                <li
                  key={point.postcode}
                  className={highlighted === point.postcode ? 'area-row is-highlighted' : 'area-row'}
                  onMouseEnter={() => setHighlighted(point.postcode)}
                  onMouseLeave={() => setHighlighted(null)}
                >
                  <MapPin size={16} aria-hidden="true" className="area-row__icon" />
                  <span className="area-row__text">
                    <span className="area-row__postcode">{point.postcode}</span>
                    <span className="small muted">
                      {tx(
                        `${qualifying} of ${certificates} certificates qualify · median band ${point.medianBand ?? '—'}`,
                        `${certificates} sertifikanın ${qualifying} tanesi uyuyor · medyan band ${point.medianBand ?? '—'}`,
                        `${qualifying} z ${certificates} świadectw spełnia warunek · mediana klasy ${point.medianBand ?? '—'}`,
                        `${qualifying} de ${certificates} certificados cumprem · classe mediana ${point.medianBand ?? '—'}`,
                        `${qualifying} de ${certificates} certificados cumplen · letra mediana ${point.medianBand ?? '—'}`,
                      )}
                    </span>
                  </span>
                  {/*
                    * Only the criteria that were asked for are shown, and only when the walk was
                    * actually measured: a row that says "station" with no distance would be
                    * asserting something the data does not contain.
                    */}
                  <span className="area-row__near small">
                    {point.school && num(point.school.distanceMetres) !== null ? (
                      <span className="area-row__fact">
                        <GraduationCap size={15} aria-hidden="true" />
                        {/* The icon carries the kind for sighted readers; the word carries it for the rest. */}
                        <span className="sr-only">
                          {tx('Nearest school:', 'En yakın okul:', 'Najbliższa szkoła:', 'Escola mais próxima:', 'Colegio más cercano:')}{' '}
                        </span>
                        {point.school.name} · {point.school.distanceMetres} m
                        {point.school.judgement ? ` · ${point.school.judgement}` : ''}
                      </span>
                    ) : null}
                    {point.station && num(point.station.distanceMetres) !== null ? (
                      <span className="area-row__fact">
                        <TrainFront size={15} aria-hidden="true" />
                        <span className="sr-only">
                          {tx('Nearest station:', 'En yakın istasyon:', 'Najbliższa stacja:', 'Estação mais próxima:', 'Estación más cercana:')}{' '}
                        </span>
                        {point.station.name} · {point.station.distanceMetres} m
                      </span>
                    ) : null}
                    {point.bus && num(point.bus.distanceMetres) !== null ? (
                      <span className="area-row__fact">
                        <Bus size={15} aria-hidden="true" />
                        <span className="sr-only">
                          {tx('Nearest bus stop:', 'En yakın otobüs durağı:', 'Najbliższy przystanek:', 'Paragem mais próxima:', 'Parada más cercana:')}{' '}
                        </span>
                        {point.bus.name} · {point.bus.distanceMetres} m
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        <p className="small muted">
          {tx(
            'These are dwellings with certificates, not homes that are for rent: ScanHome holds no listings, no rents and no availability. Take a postcode to wherever you search for a home, then bring the address back here for its report.',
            'Bunlar sertifikası olan konutlardır, kiralık ilanlar değil: ScanHome’te ilan, kira ve müsaitlik bilgisi yoktur. Bir posta kodunu ev aradığınız yere götürün, sonra adresi raporu için buraya geri getirin.',
            'To mieszkania ze świadectwami, a nie oferty najmu: ScanHome nie ma ogłoszeń, czynszów ani dostępności. Weź kod pocztowy tam, gdzie szukasz mieszkania, a potem wróć tu z adresem po raport.',
            'Estas são habitações com certificado, não casas para arrendar: o ScanHome não tem anúncios, rendas nem disponibilidade. Leve um código postal para onde procura casa e traga a morada de volta para o relatório.',
            'Son viviendas con certificado, no casas en alquiler: ScanHome no tiene anuncios, ni rentas, ni disponibilidad. Llévate un código postal a donde busques vivienda y trae la dirección aquí para su informe.',
          )}
        </p>
      </section>
    </>
  );
}
